// frontend/client/src/hooks/useMediaLibrary.js
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/apiClient';

export function useMediaLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const { currentUser } = useAuth();

  const fetchMedia = useCallback(async () => {
    if (!currentUser?.uid) {
      setError('Please log in to view your media library');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch Images, Videos, AND Database Videos simultaneously
      const [imageRes, videoRes, dbVideoRes] = await Promise.allSettled([
        fetch('/api/media/library?resource_type=image', { headers: { 'X-Firebase-UID': currentUser.uid } }).then(r => r.json()),
        fetch('/api/media/library?resource_type=video', { headers: { 'X-Firebase-UID': currentUser.uid } }).then(r => r.json()),
        api.get('/video/my-videos'),
      ]);

      let combinedMedia = [];

      // 1. Map Cloudinary Images
      if (imageRes.status === 'fulfilled' && imageRes.value.success && imageRes.value.resources) {
        combinedMedia = [...combinedMedia, ...imageRes.value.resources];
      }

      // 2. Map Cloudinary Videos (This fixes the missing video issue!)
      if (videoRes.status === 'fulfilled' && videoRes.value.success && videoRes.value.resources) {
        combinedMedia = [...combinedMedia, ...videoRes.value.resources];
      }

      // 3. Map MongoDB Processed Videos
      if (dbVideoRes.status === 'fulfilled' && dbVideoRes.value.success && dbVideoRes.value.videos) {
        const dbVideos = dbVideoRes.value.videos.map(v => ({
          ...v,
          resource_type: 'video',
          secure_url: v.processedVideo?.secure_url || v.originalVideo?.secure_url,
          public_id: v.originalVideo?.publicId || v._id,
          created_at: v.createdAt
        })).filter(v => v.secure_url); // Only keep valid URLs
        
        combinedMedia = [...combinedMedia, ...dbVideos];
      }

      // Deduplicate overlapping media by public_id or _id
      const uniqueMediaMap = new Map();
      combinedMedia.forEach(item => {
        const id = item.public_id || item._id;
        if (id && !uniqueMediaMap.has(id)) {
          uniqueMediaMap.set(id, item);
        }
      });

      const finalMedia = Array.from(uniqueMediaMap.values()).sort((a, b) => 
        new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt)
      );

      setItems(finalMedia);
    } catch (err) {
      console.error('[useMediaLibrary] Fetch error:', err);
      setError('Failed to load media.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const deleteMedia = async (publicId, type) => {
    try {
      // Optimistically remove from UI immediately
      setItems(prev => prev.filter(item => item.public_id !== publicId && item._id !== publicId));

      // Determine if this is a DB-registered video (has a real MongoDB ObjectId _id)
      // vs a Cloudinary asset (public_id is a path like "infleuince/videos/...")
      const isMongoId = /^[a-f\d]{24}$/i.test(publicId);

      if (type === 'video' && isMongoId) {
        // DB-registered video — delete from MongoDB
        await api.delete(`/video/${publicId}`);
      } else if (type === 'video') {
        // Cloudinary video with path-style publicId (e.g. infleuince/videos/xxx)
        // Step 1: Delete from Cloudinary
        const encoded = encodeURIComponent(publicId);
        await api.delete(`/media/${encoded}?resource_type=video`);
        // Step 2: Find and delete the matching MongoDB record
        // Look through current items for one that matches this publicId
        // and has a MongoDB _id — then delete that record too
        try {
          const videoList = await api.get('/video/my-videos');
          if (videoList.success && videoList.videos) {
            const match = videoList.videos.find(v => 
              v.originalVideo?.publicId === publicId ||
              v.originalVideo?.publicId?.includes(publicId.split('/').pop())
            );
            if (match?._id) {
              await api.delete(`/video/${match._id}`);
              console.log('[MediaLibrary] Cleaned up MongoDB record:', match._id);
            }
          }
        } catch (e) {
          console.warn('[MediaLibrary] MongoDB cleanup failed (non-fatal):', e.message);
        }
      } else {
        // Image: delete from Cloudinary only
        const encoded = encodeURIComponent(publicId);
        await api.delete(`/media/${encoded}?resource_type=image`);
      }

      // Refetch after 1s to confirm server state — prevents ghost items
      // appearing if Cloudinary delete succeeded but cache returns stale results
      setTimeout(() => fetchMedia(), 1000);
    } catch (err) {
      // Revert optimistic delete on failure
      fetchMedia();
      throw err;
    }
  };

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const filteredItems = items.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    const name = (item.public_id || item.name || '').toLowerCase();
    const matchesSearch = name.includes(searchLower);
    
    if (filterType === 'all') return matchesSearch;
    return matchesSearch && item.resource_type === filterType;
  });

  return {
    items: filteredItems,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    filterType,
    setFilterType,
    deleteMedia,
    refetchMedia: fetchMedia
  };
}